import { isNoDamageMode } from '../debug'
import { playBuildingDestroyed, playUnitDeath, playUnitAttack } from '../sound'
import { AnimEvent, GameState, Unit, getProjectileType } from '../types'
import { DAMAGE_FLASH_MS, BLOOD_POOL_MAX } from './constants'
import { getAttackAura } from './bonusEffects'
import { animUid, spawnUnit } from './helpers'
import { findAttackTarget, unitDist } from './targeting'
import {
  heroOutgoingDamage, heroIncomingDamage, afterHeroHit, onHeroKill,
  tempAttackBonus, chillAttackFactor,
} from './heroAbilities'

// ─── Combat Constants ─────────────────────────────────────

export const DEATH_LINGER_MS          = 1200
export const KILL_FLASH_MS            = 500
export const PROJECTILE_SPEED_PX_MS   = 0.4   // game-px per ms
export const ANIM_EVENT_PROJECTILE_MS = 600    // max projectile lifetime (cap)

// ─── Per-unit Combat ──────────────────────────────────────

export function processAttacks(s: GameState, deltaMs: number, log: string[]): void {
  const playerAtkAura   = getAttackAura(s.field, 'player')
  const opponentAtkAura = getAttackAura(s.field, 'opponent')

  for (const unit of s.field) {
    if (unit.attack === 0 || unit.hp <= 0) continue

    if (unit.attackTimer > 0) {
      unit.attackTimer -= deltaMs
      continue
    }

    // Sticky targeting: reuse locked-on target if still valid (alive + in range)
    let target: Unit | null = null
    if (unit.targetId) {
      const sticky = s.field.find(u => u.id === unit.targetId)
      if (sticky && sticky.hp > 0 && sticky.owner !== unit.owner && unitDist(unit, sticky) <= unit.attackRange * 1.5) {
        target = sticky
      } else {
        unit.targetId = undefined
      }
    }
    if (!target) {
      target = findAttackTarget(s.field, unit)
      if (target) unit.targetId = target.id
    }

    const isPlayer  = unit.owner === 'player'
    const atkAura   = isPlayer ? playerAtkAura : opponentAtkAura

    if (target) {
      if (unit.owner === 'player') playUnitAttack()
      const prevHp         = target.hp
      const bloodMoonMult  = s.activeBattleEvent?.type === 'bloodMoon' ? 2 : 1
      const targetTags     = target.tags ?? []
      const swMult         = (unit.strengths ?? []).some(t => targetTags.includes(t)) ? 1.5 : 1
      const affDmgMult     = (unit.affinityActive && unit.affinity?.effectType === 'damage')
        ? unit.affinity.effectAmount : 1
      const masteryEliteMult = (unit.masteryLevel ?? 0) >= 5 ? 1.1 : 1
      // Last Light's timed surge rides on top of the attack stat, like the attack aura does.
      const baseAttack = unit.attack + atkAura + tempAttackBonus(s, unit)
      const rawDmg = Math.round(baseAttack * bloodMoonMult * swMult * affDmgMult * masteryEliteMult)
      // Hero signature abilities: Duel / Breach on the attacker, Cross-Reference on the target.
      const dmg = heroOutgoingDamage(s, unit, target, rawDmg)

      // Emit projectile animation for ranged (bypassWall) attackers
      if (unit.bypassWall && !unit.isWall) {
        const dist    = Math.hypot(target.x - unit.x, target.y - unit.y)
        const travelMs = Math.min(ANIM_EVENT_PROJECTILE_MS, dist / PROJECTILE_SPEED_PX_MS)
        const proj: AnimEvent = {
          id: animUid(),
          kind: 'projectile',
          fromUnitId: unit.id,
          fromX: unit.x, fromY: unit.y,
          toX: target.x, toY: target.y,
          expiresAt: s.gameTime + travelMs,
          projectileType: getProjectileType(unit.tags),
          aoeRadius: unit.attackEffect?.aoeRadius,
        }
        s.animEvents.push(proj)
      }

      if (target.owner === 'player' && isNoDamageMode()) {
        log.push(`${unit.name} would damage ${target.name} (dev mode — no damage)`)
      } else {
        // Bulwark soaks part of the blow and reflects thorns at a melee attacker.
        const landed = heroIncomingDamage(s, unit, target, dmg, log)
        target.hp -= landed
        afterHeroHit(s, unit, target)
        // A thorns kill is a kill: mark the body dying so it lingers for the death
        // animation and isn't purged before the commander/base HP sync sees it, the
        // same way the AOE and half-health paths below do.
        if (unit.hp <= 0) {
          if (unit.moveSpeed > 0 && !unit.isWall && !unit.dyingTimer) unit.dyingTimer = DEATH_LINGER_MS
          if (unit.isCommander && unit.owner === 'player') {
            s.lastPlayerDamageSource = { kind: 'unit', name: target.name }
          }
        }
        const actualDamage = prevHp - Math.max(0, target.hp)
        if (isPlayer) s.playerScore += actualDamage
        else          s.opponentScore += actualDamage
        target.lastAttackerId = unit.id
        if (target.isCommander && target.owner === 'player' && dmg > 0) {
          s.lastPlayerDamageSource = { kind: 'unit', name: unit.name }
        }
        if (target.targetId && target.targetId !== unit.id && target.attack > 0) {
          target.targetId = unit.id
        }
        target.damageFlashTimer = DAMAGE_FLASH_MS
        // Apply elemental on-hit effect (burn, freeze, poison, shock, aoe, gascloud)
        const eff = unit.attackEffect
        if (eff && target.hp > 0 && Math.random() < eff.chance) {
          if (eff.type === 'burn') {
            target.burnTimer  = eff.durationMs
            target.burnDps    = eff.dps ?? 8
          } else if (eff.type === 'freeze') {
            target.freezeTimer = eff.durationMs
            target.freezeSlow  = eff.slowFactor ?? 0.35
          } else if (eff.type === 'poison') {
            target.poisonTimer = eff.durationMs
            target.poisonDps   = eff.dps ?? 5
          } else if (eff.type === 'shock') {
            target.stunTimer = Math.max(target.stunTimer ?? 0, eff.durationMs)
          } else if (eff.type === 'aoe' && eff.aoeRadius) {
            // AOE burst: deal same damage to all enemies within radius
            const aoeTargets = s.field.filter(
              e => e.owner !== unit.owner && e.hp > 0 && e.id !== target.id &&
                   Math.hypot(e.x - target.x, e.y - target.y) <= eff.aoeRadius!
            )
            for (const e of aoeTargets) {
              e.hp = Math.max(0, e.hp - dmg)
              e.damageFlashTimer = DAMAGE_FLASH_MS
              if (e.isCommander && e.owner === 'player' && dmg > 0) {
                s.lastPlayerDamageSource = { kind: 'unit', name: unit.name }
              }
              // Mark mobile kills as dying so they linger for the death animation and
              // aren't silently purged before the commander/base HP sync sees them.
              if (e.hp <= 0 && e.moveSpeed > 0 && !e.isWall && !e.dyingTimer) e.dyingTimer = DEATH_LINGER_MS
              if (isPlayer) s.playerScore += Math.min(dmg, prevHp)
              else          s.opponentScore += Math.min(dmg, prevHp)
            }
            // AOE ring visual
            s.animEvents.push({
              id: animUid(), kind: 'aoe',
              fromX: target.x, fromY: target.y,
              toX: target.x,   toY: target.y,
              expiresAt: s.gameTime + 500,
              aoeRadius: eff.aoeRadius,
            })
          } else if (eff.type === 'gascloud' && eff.aoeRadius) {
            // Drop a lingering gas cloud at the target position
            s.hazards.push({
              id: animUid(),
              x: target.x, y: target.y,
              radius: eff.aoeRadius,
              dps: eff.dps ?? 6,
              owner: unit.owner,
              expiresAt: s.gameTime + eff.durationMs,
            })
            // Cloud visual (long-lived AnimEvent used for rendering)
            s.animEvents.push({
              id: animUid(), kind: 'gascloud',
              fromX: target.x, fromY: target.y,
              toX: target.x,   toY: target.y,
              expiresAt: s.gameTime + eff.durationMs,
              aoeRadius: eff.aoeRadius,
            })
          }
        }
        s.animEvents.push({
          id: animUid(), kind: 'hit',
          fromX: target.x, fromY: target.y,
          toX: target.x,   toY: target.y,
          expiresAt: s.gameTime + 300,
        })
        // Half-health explosion: fires once when unit first drops to ≤50% HP
        if (
          !target.halfHealthFired &&
          target.halfHealthEffect &&
          target.hp > 0 &&
          target.hp <= target.maxHp / 2
        ) {
          target.halfHealthFired = true
          const { damage: aoeDmg, range: aoeRange } = target.halfHealthEffect
          const enemies = s.field.filter(
            e => e.owner !== target.owner && e.hp > 0 &&
                 Math.hypot(e.x - target.x, e.y - target.y) <= aoeRange
          )
          for (const e of enemies) {
            e.hp -= aoeDmg
            e.damageFlashTimer = DAMAGE_FLASH_MS
            // Mark mobile kills as dying so they linger for the death animation and
            // aren't silently purged before the commander/base HP sync sees them.
            if (e.hp <= 0 && e.moveSpeed > 0 && !e.isWall && !e.dyingTimer) e.dyingTimer = DEATH_LINGER_MS
          }
          log.push(`!!💥 ${target.name} erupts! AOE blast hits ${enemies.length} enemy unit${enemies.length !== 1 ? 's' : ''}!`)
        }
        if (target.hp <= 0) {
          if (target.onDeathEffect) {
            const { damage: aoeDmg, range: aoeRange } = target.onDeathEffect
            const blastTargets = s.field.filter(
              e => e.owner !== target.owner && e.hp > 0 &&
                   Math.hypot(e.x - target.x, e.y - target.y) <= aoeRange
            )
            for (const e of blastTargets) {
              e.hp = Math.max(0, e.hp - aoeDmg)
              e.damageFlashTimer = DAMAGE_FLASH_MS
              // Mark mobile kills as dying so they linger for the death animation and
              // aren't silently purged before the commander/base HP sync sees them.
              if (e.hp <= 0 && e.moveSpeed > 0 && !e.isWall && !e.dyingTimer) e.dyingTimer = DEATH_LINGER_MS
            }
            log.push(`!!💥 ${target.name} explodes! ${blastTargets.length} unit${blastTargets.length !== 1 ? 's' : ''} caught in the blast!`)
          }
          onHeroKill(unit, target, log)
          log.push(`${unit.name} destroyed ${target.name}!`)
          if (target.moveSpeed === 0) playBuildingDestroyed()
          else                        playUnitDeath()
          if (target.moveSpeed > 0 && !target.isWall) {
            target.dyingTimer = DEATH_LINGER_MS
            if (!target.flying) {
              s.bloodPools.push({ id: target.id, x: target.x, y: target.y })
              const active = s.bloodPools.filter(p => p.fadingAt === undefined)
              if (active.length > BLOOD_POOL_MAX) active[0].fadingAt = s.gameTime
            }
          }
          unit.killFlashTimer = KILL_FLASH_MS
          for (const ally of s.field) {
            if (ally.targetId === target.id) ally.targetId = undefined
          }
        }
      }
      const affSpeedMult = (unit.affinityActive && unit.affinity?.effectType === 'attackSpeed')
        ? unit.affinity.effectAmount : 1
      // Cold Snap's chill drags on the swing as well as the step.
      unit.attackTimer = unit.attackCooldownMs / affSpeedMult / chillAttackFactor(unit)
    }
  }

  // Phantom Legion exotic: each dying player unit rolls once for an instant revive
  if (s.phantomReviveChance) {
    for (const u of s.field) {
      if (u.owner === 'player' && u.hp <= 0 && u.moveSpeed > 0 && !u.isWall && !u.reviveRolled) {
        u.reviveRolled = true
        if (Math.random() < s.phantomReviveChance) {
          u.hp = Math.ceil(u.maxHp / 2)
          u.dyingTimer = undefined
          u.reviveRolled = false   // a future death gets a fresh roll
          log.push(`👻 ${u.name} refuses to fall — the Phantom Legion marches on!`)
        }
      }
    }
  }

  // Auto-revive: once per battle, restore the first dead player unit
  if (s.unitReviveHp !== undefined) {
    const dead = s.field.find(u => u.owner === 'player' && u.hp <= 0 && u.moveSpeed > 0)
    if (dead) {
      dead.hp = s.unitReviveHp === 'half' ? Math.ceil(dead.maxHp / 2) : s.unitReviveHp === 'full' ? dead.maxHp : s.unitReviveHp
      s.unitReviveHp = undefined
      log.push(`${dead.name} rises from the dead!`)
    }
  }

  // Count kills for battle stats
  for (const u of s.field) {
    if (u.hp <= 0 && u.moveSpeed > 0 && !u.isWall && !u.isDecoy) {
      if (u.owner === 'opponent') s.battleStats.playerKills++
      else                        s.battleStats.playerUnitsLost++
    }
  }

  // Spawn buildings: leave a unit behind when destroyed
  for (const u of s.field) {
    if (u.hp <= 0 && u.moveSpeed === 0 && u.structureEffect?.type === 'spawn') {
      const se = u.structureEffect as { type: 'spawn'; unitTemplate: { name: string; maxHp: number } & Parameters<typeof spawnUnit>[0]; intervalMs: number }
      const survivor = spawnUnit(se.unitTemplate, u.owner)
      survivor.x = u.x
      survivor.y = u.y
      s.field.push(survivor)
      log.push(`${u.name} collapses — a ${survivor.name} emerges from the rubble!`)
    }
  }

  s.field = s.field.filter(u => u.hp > 0 || u.isMoat || (u.dyingTimer != null && u.dyingTimer > 0))
}

